import { useState } from 'react';
import { FolderOpen, Upload, Trash2, FileAudio, FileImage, FileVideo, File } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminFiles({ files, onRefresh }) {
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileFilter, setFileFilter] = useState('all');

  const handleFileUpload = async (e, category = 'general') => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/upload?category=${category}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${file.name} uploaded successfully`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API_URL}/admin/files/${fileId}`);
      toast.success('File deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const copyFileUrl = (fileId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('audio/')) return <FileAudio className="w-5 h-5 text-purple-400" />;
    if (contentType?.startsWith('image/')) return <FileImage className="w-5 h-5 text-cyan-400" />;
    if (contentType?.startsWith('video/')) return <FileVideo className="w-5 h-5 text-pink-400" />;
    return <File className="w-5 h-5 text-gray-400" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredFiles = files.filter(f => fileFilter === 'all' || f.category === fileFilter);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-cyan-400" /> Uploaded Files
        </h2>
        <div className="flex gap-2">
          <select value={fileFilter} onChange={(e) => setFileFilter(e.target.value)} className="input-dark px-3 py-2 rounded-md">
            <option value="all">All Files</option>
            <option value="audio">Audio</option>
            <option value="image">Images</option>
            <option value="video">Video</option>
          </select>
          <label className="btn-primary cursor-pointer flex items-center gap-2" data-testid="upload-file-btn">
            <Upload className="w-4 h-4" />
            {uploadingFile ? 'Uploading...' : 'Upload File'}
            <input type="file" className="hidden" accept="audio/*,image/*,video/*,.pdf" onChange={(e) => handleFileUpload(e, 'general')} disabled={uploadingFile} />
          </label>
        </div>
      </div>

      {/* Quick Upload Buttons */}
      <div className="glass-card p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-400 mb-3">Quick Upload</h3>
        <div className="flex flex-wrap gap-3">
          <label className="btn-ghost cursor-pointer flex items-center gap-2 border border-purple-500/30 hover:border-purple-500">
            <FileAudio className="w-4 h-4 text-purple-400" /> Audio (MP3, WAV)
            <input type="file" className="hidden" accept="audio/*" onChange={(e) => handleFileUpload(e, 'audio')} disabled={uploadingFile} />
          </label>
          <label className="btn-ghost cursor-pointer flex items-center gap-2 border border-cyan-500/30 hover:border-cyan-500">
            <FileImage className="w-4 h-4 text-cyan-400" /> Image (JPG, PNG)
            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} disabled={uploadingFile} />
          </label>
          <label className="btn-ghost cursor-pointer flex items-center gap-2 border border-pink-500/30 hover:border-pink-500">
            <FileVideo className="w-4 h-4 text-pink-400" /> Video (MP4)
            <input type="file" className="hidden" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} disabled={uploadingFile} />
          </label>
        </div>
      </div>

      {/* Files List */}
      {filteredFiles.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <FolderOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Files Uploaded</h3>
          <p className="text-gray-400 mb-4">Upload music, images, or videos for your site</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map(file => (
            <div key={file.id} className="glass-card p-4 flex items-center gap-4" data-testid={`file-item-${file.id}`}>
              {getFileIcon(file.content_type)}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate">{file.original_filename}</h4>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span>{file.category}</span>
                  <span>{formatFileSize(file.size)}</span>
                  <span>{new Date(file.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => copyFileUrl(file.id)} className="btn-ghost text-sm" title="Copy URL">Copy URL</button>
                {file.content_type?.startsWith('audio/') && (
                  <audio controls className="h-8 w-32">
                    <source src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}`} type={file.content_type} />
                  </audio>
                )}
                {file.content_type?.startsWith('image/') && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}`} alt={file.original_filename} className="w-12 h-12 object-cover rounded" />
                )}
                <button onClick={() => deleteFile(file.id)} className="btn-ghost text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
